import { sectors } from '@/services/sectors';
import { buildStoreError } from '@/utils/errors';
import { buildSectorFilterOptions, DEFAULT_TABLE_META, SECTOR_CATALOG_LIMIT, toSectorOption } from '@/utils/sectors';
import { normalizeName } from '@/utils/strings';

export default {
	namespaced: true,

	state: {
		loading: false,
		list: [],
		refreshKey: 0,
		tableLoading: false,
		tableItems: [],
		tableMeta: { ...DEFAULT_TABLE_META },
	},

	getters: {
		sectors: (state) => state.list,
		sectorOptions: (state) => state.list.map(toSectorOption),
		activeSectorOptions: (state) => state.list.filter((sector) => sector.status === 'active').map(toSectorOption),
		sectorFilterOptions: (state) => buildSectorFilterOptions(state.list),
		hasSectors: (state) => state.list.length > 0,
		loading: (state) => state.loading,
		refreshKey: (state) => state.refreshKey,
		tableItems: (state) => state.tableItems,
		tableMeta: (state) => state.tableMeta,
		tableLoading: (state) => state.tableLoading,
	},

	mutations: {
		SET_SECTORS(state, list) { state.list = list; },
		SET_LOADING(state, value) { state.loading = value; },
		INCREMENT_REFRESH_KEY(state) { state.refreshKey += 1; },
		SET_TABLE_ITEMS(state, { items, meta }) {
			state.tableItems = items;
			state.tableMeta = meta;
		},
		SET_TABLE_LOADING(state, value) { state.tableLoading = value; },
	},

	actions: {
		refreshSectors({ commit }) {
			commit('INCREMENT_REFRESH_KEY');
		},

		async fetchSectors({ commit }, companyId) {
			commit('SET_LOADING', true);
			try {
				const { data } = await sectors.getSectors(companyId, { perPage: SECTOR_CATALOG_LIMIT });
				commit('SET_SECTORS', data?.data || []);
			} catch (error) {
				console.warn('[sectorsModule/fetchSectors]', error);
				throw buildStoreError(error, 'Não foi possível carregar os setores.');
			} finally {
				commit('SET_LOADING', false);
			}
		},

		async fetchSectorsTable({ commit }, { companyId, search, status, page = 1, perPage = DEFAULT_TABLE_META.perPage }) {
			commit('SET_TABLE_LOADING', true);
			try {
				const params = { page, perPage, search };
				if (status && status !== 'all') params.status = status;

				const { data } = await sectors.getSectors(companyId, params);
				commit('SET_TABLE_ITEMS', {
					items: data?.data || [],
					meta: data?.meta || { ...DEFAULT_TABLE_META, page, perPage },
				});
			} catch (error) {
				console.warn('[sectorsModule/fetchSectorsTable]', error);
				throw buildStoreError(error, 'Não foi possível carregar os setores.');
			} finally {
				commit('SET_TABLE_LOADING', false);
			}
		},

		// Nome normalizado como no vcpode-web: os dois sistemas gravam o mesmo dado e o Backend não detecta duplicidade por caixa.
		async createSector(_, { companyId, payload }) {
			try {
				const { data } = await sectors.createSector(companyId, { ...payload, name: normalizeName(payload.name) });
				return data;
			} catch (error) {
				console.warn('[sectorsModule/createSector]', error);
				throw buildStoreError(error, 'Não foi possível criar o setor.');
			}
		},

		async updateSector(_, { companyId, sectorId, payload }) {
			try {
				const body = payload.name === undefined ? payload : { ...payload, name: normalizeName(payload.name) };
				const { data } = await sectors.updateSector(companyId, sectorId, body);
				return data;
			} catch (error) {
				console.warn('[sectorsModule/updateSector]', error);
				throw buildStoreError(error, 'Não foi possível atualizar o setor.');
			}
		},

		async transferEmployees(_, { companyId, sectorId, targetSectorId }) {
			try {
				const { data } = await sectors.transferEmployees(companyId, sectorId, targetSectorId);
				return data;
			} catch (error) {
				console.warn('[sectorsModule/transferEmployees]', error);
				throw buildStoreError(error, 'Não foi possível transferir os colaboradores.');
			}
		},

		async assignSector(_, { companyId, beneficiaryId, sectorId }) {
			try {
				const { data } = await sectors.setBeneficiarySector(companyId, beneficiaryId, sectorId);
				return data;
			} catch (error) {
				console.warn('[sectorsModule/assignSector]', error);
				throw buildStoreError(error, 'Não foi possível atribuir o setor.');
			}
		},
	},
};
